import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { motion } from 'motion/react'
import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from 'react'

type DropdownProps = {
  children: ReactNode
  open?: boolean
  setOpen?: Dispatch<SetStateAction<boolean>>
}

type TriggerProps = {
  children: ReactNode
  className?: string
  onBlur?: () => void
}

type ContentProps = {
  children: ReactNode
  align?: 'start' | 'end' | 'center'
  container?: HTMLElement
}

type ListItemProps = {
  children: ReactNode
  onSelect?: (value: string) => void
  className?: string
  disabled?: boolean
  value?: string
}

type DropdownContext = {
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
}

type DropdownContentContext = {
  close: () => void
}

const DropdownContext = createContext<DropdownContext>({
  open: false,
  setOpen: () => {},
})

export function Dropdown({ children, setOpen, open }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const controlledOpen = open ?? isOpen;
  const controlledSetOpen = setOpen ?? setIsOpen;

  return (
    <DropdownContext.Provider value={{ open: controlledOpen, setOpen: controlledSetOpen }}>
      <DropdownMenu.Root
        open={controlledOpen}
        onOpenChange={controlledSetOpen}
      >
        {children}
      </DropdownMenu.Root>
    </DropdownContext.Provider>
  );
}

function Trigger({ children, className, onBlur }: TriggerProps) {
  return (
    <DropdownMenu.Trigger asChild>
      <button
        className={className}
        onBlur={onBlur}
      >
        {children}
      </button>
    </DropdownMenu.Trigger>
  );
}

const DropdownContentContext = createContext<DropdownContentContext>({
  close: () => {},
})

function Content({ children, align, container }: ContentProps) {
  return (
    <DropdownMenu.Portal container={container || document.body}>
      <DropdownMenu.Content align={align} className="bg-white z-[9999]" sideOffset={5}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
        >
          {children}
        </motion.div>
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  );
}

// function Content({ children, align, container }: ContentProps) {
//   const { open, setOpen } = useContext(DropdownContext);
//
//   console.log("Dropdown content open:", open);
//
//   return open ? (
//     <DropdownMenu.Portal container={container || document.body}>
//       <DropdownMenu.Content align={align} className="border p-2 bg-white">
//         {children}
//       </DropdownMenu.Content>
//     </DropdownMenu.Portal>
//   ) : null;
// }

function ListItem({
  children,
  className,
  onSelect,
  disabled,
  value = '',
}: ListItemProps) {
  const { close } = useContext(DropdownContentContext)
  return (
    <DropdownMenu.Item
      asChild
      disabled={disabled}
      className={className}
      onSelect={() => {
        onSelect?.(value)
        close()
      }}
    >
      {children}
    </DropdownMenu.Item>
  )
}

function Separator() {
  return <DropdownMenu.Separator className="border-b border-grey-100" />
}

Dropdown.Trigger = Trigger
Dropdown.Content = Content
Dropdown.ListItem = ListItem
Dropdown.Separator = Separator
